import React from 'react';
import styled, { css } from 'styled-components';
import Radio from './Radio';
import { cssNum } from '../../helpers';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
	selected?: boolean;
	disabled?: boolean;

	useSelectedBorder?: boolean;
	useHoverEffect?: boolean;

	variant?: 'radio-card';
	selectedBackground?: string;

	padding?: string;
	minWidth?: string;
	maxWidth?: string;
	width?: string;
	borderWidth?: string;

	squareCard?: boolean;
	leftHighlightColor?: string;
}

const StyledCard = styled.div<
	React.HTMLAttributes<HTMLDivElement> &
		CardProps & {
			hasClick: boolean;
		}
>`
	border-radius: var(--radius-sm, 8px);
	border: ${(props) => (props.borderWidth ? cssNum(props.borderWidth) : props.useSelectedBorder ? '1px' : '2px')} solid var(--border, #e2e6ed);
	padding: ${(props) => (props.padding ? cssNum(props.padding) : '24px')};
	transition: all 0.2s ease;
	background-color: ${(props) => (props.selected ? props.selectedBackground : 'var(--surface-primary)')};
	opacity: ${(props) => (props.disabled ? 0.5 : 1)};
	width: ${(props) => cssNum(props.width ?? 'auto')};
	min-width: ${(props) => cssNum(props.minWidth ?? 'auto')};
	max-width: ${(props) => cssNum(props.maxWidth ?? 'auto')};

	// Selected border
	${(props) => {
		if (!props.useSelectedBorder) {
			return css``;
		}
		if (props.selected) {
			return css`
				border: ${props.borderWidth ? cssNum(props.borderWidth) : '1px'} solid var(--brand-primary);
			`;
		}
		return css`
			&:hover {
				border: ${props.borderWidth ? cssNum(props.borderWidth) : '1px'} solid var(--brand-primary);
			}
		`;
	}}
	${(props) => {
		if (!props.hasClick || props.disabled) {
			return css``;
		}
		return css`
			cursor: pointer;

			&:hover {
				${props.useHoverEffect &&
				css`
					box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
					transform: translateY(-2px);
				`}
			}

			&:active {
				${props.useHoverEffect &&
				css`
					box-shadow: 0 4px 12px var(--box-shadow, rgba(0, 0, 0, 0.05));
					transform: translateY(-4px);
				`}
			}
		`;
	}}

	${(props) =>
		props.squareCard &&
		css`
			border-radius: 0;
		`}

	${(props) =>
		!!props.leftHighlightColor &&
		css`
			border-top: none;
			border-right: none;
			border-bottom: none;
			border-left: 6px solid ${props.leftHighlightColor};
		`}
`;

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ selected, onClick, disabled, ...props }, ref) => {
	const onClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
		if (disabled) {
			e.preventDefault();
			return;
		}

		// If the element they clicked as actually the "preview-link" element, don't run the onClick function
		if ((e.target as HTMLElement).classList.contains('preview-link')) {
			return;
		}

		onClick?.(e);
	};

	return (
		<StyledCard
			ref={ref}
			selected={selected}
			disabled={disabled}
			hasClick={!!onClick}
			onClick={onClickHandler}
			{...props}
		>
			{props.variant === 'radio-card' && (
				<>
					<div
						css={`
							display: flex;
							align-items: center;
							justify-content: center;
						`}
					>
						<Radio selected={selected ?? false} />
					</div>
				</>
			)}
			{props.children}
		</StyledCard>
	);
});

export default Card;
